// @ts-nocheck
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@store/Overrides/Components/SmallHeadingAndText";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { formFieldQtyFieldStyles } from "@store/Themes/common/mixins/formField";
import { productLineV2Props } from "@store/Themes/common/props/productLineV2";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { FC, useState } from "react";
import { css } from "styled-components";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import { orderV2Props } from "@store/Themes/common/props/orderV2";

export interface ProductLineQuantityPresentationProps extends ExpandStyles<ProductLineQuantityStyles> {}

export interface ProductLineQuantityProps extends ProductLineQuantityPresentationProps {
    canEdit?: boolean;
    isDisabled?: boolean;
    label?: string;
    onChange?: (quantity: number) => void;
    quantity?: number;
    previousValue?: string | number;
}

export interface ProductLineQuantityStyles {
    textField?: TextFieldPresentationProps;
    smallHeadingAndText?: SmallHeadingAndTextStyles;
    changeTooltipAlert?: TooltipPresentationProps;
}

export const productLineQuantityStyles: ProductLineQuantityStyles = {
    textField: mergeProps<ProductLineQuantityStyles["textField"]>(productLineV2Props.infoFormField, {
        mergeCss: true,
        cssOverrides: {
            inputSelect: css`
                ${formFieldQtyFieldStyles()}
            `,
        },
    }),
    smallHeadingAndText: productLineV2Props.infoSmallHeadingAndText,
    changeTooltipAlert: orderV2Props.changeTooltip,
};

const ProductLineQuantity: FC<ProductLineQuantityProps> = ({
    canEdit,
    isDisabled,
    label,
    onChange,
    quantity,
    previousValue,
    extendedStyles,
    overridedStyles,
}) => {
    const styles = useExpandProps(productLineQuantityStyles, extendedStyles, overridedStyles);
    const [submitSyncVersion, setSubmitSyncVersion] = useState(0);
    const previousValueString = previousValue?.toString();

    const onSubmit = (value: string, onError?: () => void) => {
        const parsedQuantity = parseInt(value, 10);

        if (Number.isNaN(parsedQuantity)) {
            onError?.();
            setSubmitSyncVersion(current => current + 1);
            return;
        }

        onChange?.(parsedQuantity);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        `${quantity ?? ""}`,
        onSubmit,
        submitSyncVersion,
    );

    if (canEdit) {
        return (
            <TextField
                {...styles.textField}
                type="number"
                min={0}
                max={999999}
                label={label ?? translate("QTY")}
                value={value}
                disabled={isDisabled}
                onChange={changeHandler}
                onKeyDown={keyDownHandler}
                onBlur={blurHandler}
                onClick={event => event.currentTarget.focus()}
            />
        );
    }

    return (
        <SmallHeadingAndText
            extendedStyles={styles.smallHeadingAndText}
            heading={label ?? translate("QTY")}
            text={
                <>
                    {value}
                    {previousValueString && previousValueString !== value && (
                        <Tooltip
                            text={translate(
                                `Ordered Quantity of this item has changed from {0} to {1}`,
                                previousValueString,
                                value,
                            )}
                            {...styles.changeTooltipAlert}
                        />
                    )}
                </>
            }
            useAriaLabel={true}
        />
    );
};

export default ProductLineQuantity;
